import { useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Button, Icon, Text, VStack } from '@chakra-ui/react';
import { Country, DonationField, PaymentSchedule } from '../../constants';
import {
  buildFormSchema,
  getDataTracking,
  setFormStartEvent,
} from '../../utils';
import { useAppProvider } from '../../context';
import { DonationFormType, useDonationForm } from '../../hooks';
import {
  BillingAddress,
  DonateByMail,
  DonorRecognition,
  DoubleTheDonationSearch,
  YourInfo,
} from '../../components';
import { DonationArray } from '../../components/payment/DonationArray';
import { LockClosed } from '../../assets';

export type DonationFormProps = {
  isLoading?: boolean;
  handleFormSubmit: (data: DonationFormType) => void;
};

export const DonationForm = ({
  handleFormSubmit,
  isLoading,
}: DonationFormProps) => {
  const [hasUserModifiedDisplayName, setHasUserModifiedDisplayName] =
    useState(false);
  const [isOtherAmountActive, setIsOtherAmountActive] = useState(false);

  const {
    data: { user },
  } = useAppProvider();
  const { getOtherLevelId, getDefaultIndexValue } = useDonationForm();

  const defaultFormValues: DonationFormType = useMemo(
    () => ({
      [DonationField.paymentSchedule]: PaymentSchedule.OneTime,
      [DonationField.donationAmount]: getDefaultIndexValue(),
      [DonationField.donationOtherAmount]: '',
      [DonationField.donationLevelId]: getOtherLevelId(),
      [DonationField.country]: Country.UnitedStates,
      [DonationField.firstName]: user?.name?.first || '',
      [DonationField.lastName]: user?.name?.last || '',
      [DonationField.donorEmail]: user?.email?.primary_address || '',
      [DonationField.donorEmailOptIn]: false,
      [DonationField.address1]: user?.primary_address?.street1 || '',
      [DonationField.address2]: user?.primary_address?.street2 || '',
      [DonationField.address3]: user?.primary_address?.street3 || '',
      [DonationField.city]: user?.primary_address?.city || '',
      [DonationField.state]: user?.primary_address?.state || '',
      [DonationField.postCode]: user?.primary_address?.zip || '',
      [DonationField.donationDisplayName]: '',
      [DonationField.donationPersonalNote]: '',
      [DonationField.hideDonationAmount]: false,
      [DonationField.hideMyDisplayName]: false,
    }),
    [
      user?.email?.primary_address,
      user?.name?.first,
      user?.name?.last,
      user?.primary_address?.city,
      user?.primary_address?.state,
      user?.primary_address?.street1,
      user?.primary_address?.street2,
      user?.primary_address?.street3,
      user?.primary_address?.zip,
    ]
  );

  const formMethods = useForm({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
    resolver: yupResolver(
      buildFormSchema({
        isOtherDonation: isOtherAmountActive,
      })
    ),
  });
  const { control, handleSubmit } = formMethods;
  const [donationAmount] = useWatch({
    name: [DonationField.donationAmount],
    control,
  });

  return (
    <FormProvider {...formMethods}>
      <form
        onClickCapture={() => setFormStartEvent()}
        noValidate
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <VStack spacing={10} w="full" align="stretch">
          {/* <DonationSchedule /> */}
          <DonationArray
            isOtherAmountActive={isOtherAmountActive}
            handleOtherFocus={() => setIsOtherAmountActive(true)}
            handleOtherActive={() => setIsOtherAmountActive(false)}
          />
          <DonateByMail />
          <YourInfo />
          <BillingAddress />
          <DoubleTheDonationSearch />
          <DonorRecognition
            hasUserModifiedDisplayName={hasUserModifiedDisplayName}
            displayNameModified={() => setHasUserModifiedDisplayName(true)}
          />
          <Button
            {...getDataTracking('user-donated')}
            fontWeight="bold"
            size="lg"
            type="submit"
            leftIcon={<Icon as={LockClosed} h={6} w={8} />}
            isLoading={isLoading}
          >
            Complete Payment
            <Text>&nbsp;${donationAmount}</Text>
          </Button>
        </VStack>
      </form>
    </FormProvider>
  );
};
